import type { FC } from 'react'

import { Checkbox, Select, Textarea } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { useQuery } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, FormikProvider, useFormik } from 'formik'
import * as yup from 'yup'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

import queries from '../../queries'
import type { SendSMS } from '../../resources'
import adminResources from '../../resources'

type Props = {
  scrollToNextPage?: (index: number) => void
  onSend: (values: Omit<SendSMS, 'receivers'>) => void
  isLoading: boolean
  canGoBack: boolean
}

const MAX_CHARS = 160

export const ComposeSMSForm: FC<Props> = ({
  scrollToNextPage,
  onSend,
  isLoading,
  canGoBack = true,
}) => {
  const translate = useTranslation()

  const translatedMandatoryMessage = translate('This field is mandatory')
  const translatedLengthMessage = (len: number) =>
    translate(`The length of the message must be less than ${len} characters`)

  const validationSchema = yup.object({
    sender: yup.string().required(translatedMandatoryMessage),
    message: yup
      .string()
      .max(500, obj => translatedLengthMessage(obj.max))
      .required(translatedMandatoryMessage),
    isPrivate: yup.boolean().required(translatedMandatoryMessage),
  })

  const userAliases = useQuery(queries.adminUserAliasesList(), adminResources.getUserAliasesList)

  const formik = useFormik({
    initialValues: {
      sender: '',
      message: '',
      isPrivate: false,
    },
    validationSchema,
    onSubmit: onSend,
  })

  return (
    <div className="flex grow flex-col">
      <FormikProvider value={formik}>
        <Form className="flex h-full flex-col gap-3">
          <div className="flex w-full flex-wrap gap-x-3 gap-y-0">
            <h4 className="mb-1 w-min pt-3">
              <Translation id="From:" />
            </h4>
            <div className="flex w-full grow flex-row gap-2 md:w-auto">
              <div className="w-full shrink md:w-[20rem]">
                <Field name="sender">
                  {({ field, form, meta }: FieldProps) => (
                    <Select
                      id="sender"
                      options={userAliases.data || []}
                      {...field}
                      onChange={option => form.setFieldValue(field.name, option?.value)}
                      error={meta.error}
                      touched={meta.touched}
                    />
                  )}
                </Field>
              </div>
              <div className="flex items-center">
                <Field name="isPrivate">
                  {({ field, form }: FieldProps) => (
                    <Checkbox
                      id="isPrivate"
                      marginTop={0}
                      label={<Translation id="Is private message?" />}
                      checked={field.value}
                      {...field}
                      onChange={event => form.setFieldValue(field.name, event.target.checked)}
                    />
                  )}
                </Field>
              </div>
            </div>
          </div>
          <Field name="message">
            {({ field, meta }: FieldProps) => (
              <div className="grow p-1">
                <Textarea
                  id="message"
                  isFullWidth
                  isFullHeight
                  className="resize-none"
                  error={meta.error}
                  touched={meta.touched}
                  {...field}
                />
              </div>
            )}
          </Field>
          <div className="space-between flex h-max w-full flex-col gap-2 md:flex-row">
            <div className="flex w-full items-end justify-between gap-1 px-2 md:flex-col md:items-start md:justify-center md:px-4">
              <span>
                <Translation id="Character count:" /> {formik.values.message.length % MAX_CHARS} /{' '}
                {MAX_CHARS}
              </span>
              {formik.values.message.length > MAX_CHARS && (
                <div className="text-primary">
                  <Translation id="Messages count: " />{' '}
                  {Math.ceil(formik.values.message.length / MAX_CHARS)}
                </div>
              )}
            </div>
            <div className="flex h-max w-full justify-between gap-1 md:justify-end">
              {canGoBack && (
                <Button
                  className="ml-0 w-1/2 md:ml-2 md:w-24"
                  onClick={() => scrollToNextPage?.(0)}
                  disabled={isLoading}
                >
                  <Translation id="Back" />
                </Button>
              )}
              <Button
                className="mr-0 w-1/2 md:mr-2 md:w-24"
                variant="primary"
                type="submit"
                isLoading={isLoading}
              >
                <Translation id="Send" />
              </Button>
            </div>
          </div>
        </Form>
      </FormikProvider>
    </div>
  )
}
